import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import { tafqeet } from '../../utils/tafqeet';
import { formatDate, formatDateTime } from '../../utils/date';

/**
 * ReceiptModal — معاينة وطباعة الوصولات والسندات الرسمية (مقاس A5 عمودي)
 *
 * يُستخدم لنوعين من العمليات:
 *  - سند قبض (direction: 'in')  — استلام قسط / استلام مبلغ / استلام صندوق
 *  - سند صرف (direction: 'out') — دفع من الصندوق
 *
 * البيانات تُمرّر عبر الخاصية `receipt`:
 * {
 *   direction: 'in' | 'out',
 *   docTitle:  'سند قبض',                     // العنوان الرسمي على السند
 *   subtitle:  'استلام قسط',                  // وصف نوع العملية
 *   serial:    'INS-001042',                  // رقم السند
 *   date:      '2026-08-20',                  // تاريخ العملية
 *   partyLabel:'استلمنا من السيد',
 *   partyName: 'محمد عبدالله',
 *   details:   [{ label, value }],            // بيانات تعريفية
 *   amount:    1500000,
 *   summary:   [{ label, value, tone }],      // ملخص محاسبي اختياري
 *   notes:     'ملاحظات',
 * }
 */

// بيانات الشركة الظاهرة في ترويسة السند.
// عدّل الهاتف/العنوان هنا لتظهر في كل الوصولات (اتركها فارغة لإخفائها).
const COMPANY = {
  name: 'الإجابة للحج والعمرة',
  tagline: 'نظام إدارة المجموعات',
  phone: '',
  address: '',
  logo: '/ajaba-logo.png',
};

const CURRENCY = 'د.ع';

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const formatPrintedAt = () => formatDateTime(new Date());

export default function ReceiptModal({ open, onClose, receipt }) {
  // مقاس الورق A5 يُحقن فقط أثناء فتح السند، حتى لا يؤثر على طباعة بقية صفحات التطبيق
  useEffect(() => {
    if (!open) return undefined;
    const style = document.createElement('style');
    style.id = 'rcpt-page-size';
    style.textContent = '@page { size: A5 portrait; margin: 0; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, [open]);

  const handlePrint = useCallback(() => {
    document.body.classList.add('rcpt-printing');
    const cleanup = () => {
      document.body.classList.remove('rcpt-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    // احتياط للمتصفحات التي لا تُطلق afterprint بشكل موثوق
    setTimeout(cleanup, 1000);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open || !receipt) return null;

  const {
    direction = 'in',
    docTitle,
    subtitle,
    serial,
    date,
    partyLabel,
    partyName,
    details = [],
    amount = 0,
    summary = [],
    notes,
  } = receipt;

  const isOut = direction === 'out';
  const title = docTitle || (isOut ? 'سند صرف' : 'سند قبض');
  // في سند القبض نستلم من المسلِّم، وفي سند الصرف نسلّم إلى المستلم
  const counterpartyRole = isOut ? 'المستلم' : 'المسلِّم';

  return createPortal(
    <div className="rcpt-portal fixed inset-0 z-[60] flex flex-col bg-slate-900/70 backdrop-blur-sm">
      {/* شريط أدوات المعاينة — لا يُطبع */}
      <div className="rcpt-no-print flex items-center justify-between gap-3 bg-white dark:bg-gray-900 px-4 py-3 shadow-lg">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            معاينة الوصل قبل الطباعة
          </h2>
          <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">
            مقاس A5 — {title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <Printer className="h-4 w-4" />
            طباعة
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <X className="h-4 w-4" />
            إغلاق
          </button>
        </div>
      </div>

      {/* منطقة المعاينة القابلة للتمرير */}
      <div className="rcpt-scroll flex-1 overflow-auto p-4 sm:p-8">
        <div className={`rcpt-sheet ${isOut ? 'is-out' : 'is-in'}`}>
          {/* ─── الترويسة ─── */}
          <header className="rcpt-head">
            <div className="rcpt-brand">
              <img src={COMPANY.logo} alt="" className="rcpt-logo" />
              <div>
                <div className="rcpt-brand-name">{COMPANY.name}</div>
                {COMPANY.tagline && <div className="rcpt-brand-tag">{COMPANY.tagline}</div>}
                {COMPANY.phone && <div className="rcpt-brand-tag">هاتف: {COMPANY.phone}</div>}
                {COMPANY.address && <div className="rcpt-brand-tag">{COMPANY.address}</div>}
              </div>
            </div>

            <div className="rcpt-meta">
              <div className="rcpt-meta-title">{title}</div>
              <div className="rcpt-meta-row">
                <span>رقم السند</span>
                <strong>{serial || '—'}</strong>
              </div>
              <div className="rcpt-meta-row">
                <span>التاريخ</span>
                <strong>{formatDate(date)}</strong>
              </div>
            </div>
          </header>

          {/* ─── شريط نوع العملية ─── */}
          <div className="rcpt-banner">{subtitle || title}</div>

          {/* ─── الطرف الآخر ─── */}
          <div className="rcpt-party">
            <span className="rcpt-party-label">{partyLabel || (isOut ? 'صرفنا إلى السيد' : 'استلمنا من السيد')}</span>
            <span className="rcpt-party-name">{partyName || '—'}</span>
          </div>

          {/* ─── بيانات العملية ─── */}
          {details.length > 0 && (
            <dl className="rcpt-details">
              {details.map((item) => (
                <div key={item.label} className="rcpt-detail">
                  <dt>{item.label}</dt>
                  <dd>{item.value ?? '—'}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* ─── المبلغ رقماً وكتابةً ─── */}
          <section className="rcpt-amount">
            <div className="rcpt-amount-row">
              <span className="rcpt-amount-label">{isOut ? 'المبلغ المصروف' : 'المبلغ المستلم'}</span>
              <span className="rcpt-amount-value">
                {formatMoney(amount)} <small>{CURRENCY}</small>
              </span>
            </div>
            <div className="rcpt-amount-words">{tafqeet(amount)}</div>
          </section>

          {/* ─── الملخص المحاسبي ─── */}
          {summary.length > 0 && (
            <table className="rcpt-summary">
              <tbody>
                {summary.map((item) => (
                  <tr key={item.label} className={item.tone ? `is-${item.tone}` : undefined}>
                    <th scope="row">{item.label}</th>
                    <td>
                      {formatMoney(item.value)} <small>{CURRENCY}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ─── الملاحظات ─── */}
          <div className="rcpt-notes">
            <span className="rcpt-notes-label">البيان / الملاحظات</span>
            <p>{notes?.trim() ? notes : '—'}</p>
          </div>

          {/* ─── التواقيع ─── */}
          <footer className="rcpt-foot">
            <div className="rcpt-signs">
              <div className="rcpt-sign">
                <span className="rcpt-sign-line" />
                <span className="rcpt-sign-label">المحاسب</span>
              </div>
              <div className="rcpt-sign">
                <span className="rcpt-sign-line" />
                <span className="rcpt-sign-label">أمين الصندوق</span>
              </div>
              <div className="rcpt-sign">
                <span className="rcpt-sign-line" />
                <span className="rcpt-sign-label">{counterpartyRole}</span>
              </div>
            </div>
            <div className="rcpt-fineprint">
              <span>هذا السند صادر إلكترونياً من {COMPANY.name} ويُعتمد بعد التوقيع والختم.</span>
              <span>تاريخ الطباعة: {formatPrintedAt()}</span>
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
}
